import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { quickSearch } from '../services/globalSearchService';
import { GLOBAL_SEARCH_MIN_QUERY_LENGTH } from '../helper/constants';
import useDebouncedValue from './useDebouncedValue';

const DEBOUNCE_MS = 300;
const CACHE_TTL_MS = 10 * 1000;
const CACHE_MAX_ENTRIES = 20;
const NETWORK_RETRY_DELAY_MS = 500;
const MAX_RESULTS_DEFAULT = 10;

const memoryCache = new Map();

const cacheGet = (key) => {
    const entry = memoryCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        memoryCache.delete(key);
        return null;
    }
    memoryCache.delete(key);
    memoryCache.set(key, entry);
    return entry.value;
};

const cacheSet = (key, value) => {
    if (memoryCache.size >= CACHE_MAX_ENTRIES) {
        const oldestKey = memoryCache.keys().next().value;
        memoryCache.delete(oldestKey);
    }
    memoryCache.set(key, { value, timestamp: Date.now() });
};

export const invalidateQuickSearchCache = () => {
    memoryCache.clear();
};

const isNetworkErrorWithoutResponse = (error) => {
    if (axios.isCancel(error)) return false;
    if (!error) return false;
    return !error.response;
};

const isTransientServerError = (error) => {
    if (!error || !error.response) return false;
    const status = error.response.status;
    return status === 500 || status === 502 || status === 503 || status === 504;
};

const isRetriableError = (error) =>
    isNetworkErrorWithoutResponse(error) || isTransientServerError(error);

const useGlobalQuickSearch = (query, maxResults = MAX_RESULTS_DEFAULT) => {
    const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
    const trimmed = (debouncedQuery ?? '').trim();
    const shouldSearch = trimmed.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH;

    const [state, setState] = useState({
        loading: false,
        error: null,
        results: [],
        hasMore: false
    });

    const abortRef = useRef(null);
    const [refetchCounter, setRefetchCounter] = useState(0);

    const refetch = useCallback(() => {
        setRefetchCounter((n) => n + 1);
    }, []);

    useEffect(() => {
        if (!shouldSearch) {
            if (abortRef.current) {
                abortRef.current.abort();
                abortRef.current = null;
            }
            setState({ loading: false, error: null, results: [], hasMore: false });
            return undefined;
        }

        const cacheKey = `${maxResults}|${trimmed.toLowerCase()}`;
        const cached = cacheGet(cacheKey);
        if (cached) {
            setState({ loading: false, error: null, results: cached.results, hasMore: cached.hasMore });
            return undefined;
        }

        if (abortRef.current) {
            abortRef.current.abort();
        }
        const controller = new AbortController();
        abortRef.current = controller;

        const attempt = (retriesLeft) => {
            setState((prev) => ({ ...prev, loading: true, error: null }));

            quickSearch(trimmed, maxResults, { signal: controller.signal })
                .then((response) => {
                    const payload = response.data?.data ?? response.data ?? {};
                    const results = payload.results ?? [];
                    const hasMore = Boolean(payload.hasMore);

                    cacheSet(cacheKey, { results, hasMore });
                    setState({ loading: false, error: null, results, hasMore });
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        return;
                    }

                    if (isRetriableError(error) && retriesLeft > 0) {
                        setTimeout(() => {
                            if (!controller.signal.aborted) {
                                attempt(retriesLeft - 1);
                            }
                        }, NETWORK_RETRY_DELAY_MS);
                        return;
                    }

                    setState({ loading: false, error, results: [], hasMore: false });
                });
        };

        attempt(1);

        return () => {
            controller.abort();
        };
    }, [trimmed, shouldSearch, maxResults, refetchCounter]);

    return { ...state, refetch };
};

export default useGlobalQuickSearch;
